import type { PaletteMode, Theme } from '@mui/material/styles'
import { badgeTokensDark } from '@/theme/badge-tokens'

/**
 * Design tokens copiés depuis la maquette de handoff design
 * (desktop/design_handoff_wizard) pour reproduire fidèlement son rendu.
 */
export type NfTokens = {
  green: string
  greenTint: string
  greenTint2: string
  darkGreen: string
  darkGreenHover: string
  ink: string
  ink2: string
  ink3: string
  muted: string
  muted2: string
  line: string
  line2: string
  danger: string
  dangerBg: string
  dangerBorder: string
  dangerText: string
  warn: string
  warnBg: string
  warnBorder: string
  warnText: string
  ok: string
  requiredText: string
  requiredBg: string
  surface: string
  surfaceMuted: string
  surfaceDisabled: string
  dash: string
  onGreen: string
  radiusField: number
  radiusCard: number
  radiusPill: number
  fieldHeight: number
}

const nfLayout = {
  radiusField: 8,
  radiusCard: 10,
  radiusPill: 99,
  fieldHeight: 36,
} as const

export const nfLight: NfTokens = {
  green: '#06B7B2',
  greenTint: '#E6F7F6',
  greenTint2: '#C6EBF0',
  darkGreen: '#06766F',
  darkGreenHover: '#056961',
  ink: '#111927',
  ink2: '#1F2937',
  ink3: '#374151',
  muted: '#6B7280',
  muted2: '#9AA3AE',
  line: '#E5E9EE',
  line2: '#EEF1F4',
  danger: '#D64545',
  dangerBg: '#FDEDED',
  dangerBorder: '#EDC9C9',
  dangerText: '#9C2B2B',
  warn: '#C98A2A',
  warnBg: '#FFF6E6',
  warnBorder: '#F0DDB4',
  warnText: '#8A6320',
  ok: '#2F9E7A',
  requiredText: '#B02B2B',
  requiredBg: '#FDEDED',
  surface: '#fff',
  surfaceMuted: '#FBFCFD',
  surfaceDisabled: '#F4F6F9',
  dash: '#CBD5DF',
  onGreen: '#fff',
  ...nfLayout,
}

export const nfDark: NfTokens = {
  green: '#06B7B2',
  greenTint: 'rgba(6, 183, 178, 0.18)',
  greenTint2: 'rgba(6, 183, 178, 0.28)',
  darkGreen: '#5EEAD4',
  darkGreenHover: '#05948F',
  ink: '#EDF2F7',
  ink2: '#E2E8F0',
  ink3: '#CBD5E0',
  muted: '#A0AEC0',
  muted2: '#718096',
  line: '#2D3748',
  line2: 'rgba(255, 255, 255, 0.08)',
  danger: badgeTokensDark.danger.fg,
  dangerBg: badgeTokensDark.danger.bg,
  dangerBorder: 'rgba(230, 59, 59, 0.35)',
  dangerText: badgeTokensDark.danger.fg,
  warn: badgeTokensDark.warn.fg,
  warnBg: badgeTokensDark.warn.bg,
  warnBorder: 'rgba(247, 144, 9, 0.35)',
  warnText: badgeTokensDark.warn.fg,
  ok: '#34D399',
  requiredText: badgeTokensDark.required.fg,
  requiredBg: badgeTokensDark.required.bg,
  surface: '#111927',
  surfaceMuted: '#1C2536',
  surfaceDisabled: '#1C2536',
  dash: '#4A5568',
  onGreen: '#fff',
  ...nfLayout,
}

/** Tokens light (maquette). Préférer `getNf(theme)` pour un rendu thème-aware. */
export const nf = nfLight

export const getNf = (themeOrMode: Theme | PaletteMode): NfTokens => {
  const mode = typeof themeOrMode === 'string' ? themeOrMode : themeOrMode.palette.mode
  return mode === 'dark' ? nfDark : nfLight
}

export const nfTitleFont = "'Plus Jakarta Sans', Inter, sans-serif"
